import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { memberApi } from '@formosa/shared/api/member';
import { paymentApi } from '@formosa/shared/api/payment';
import { ApiError } from '@formosa/shared/api/request';
import AddressQrCode from '../../components/deposit/AddressQrCode';
import DepositSuccessModal from '../../components/deposit/DepositSuccessModal';
import {
  DEFAULT_QUICK_AMOUNTS,
  copyText,
  formatDepositAmount,
  resolveImageUrl,
} from '../../constants/depositConstants';
import { tf } from '../../constants/fundConstants';
import { useTranslation } from '../../i18n/LocaleContext';

export default function OfflineDepositPage() {
  const navigate = useNavigate();
  const { addressId } = useParams();
  const location = useLocation();
  const { t } = useTranslation();
  const fileInputRef = useRef(null);

  const [address, setAddress] = useState(location.state?.address || null);
  const [amount, setAmount] = useState('');
  const [receiptPhoto, setReceiptPhoto] = useState('');
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(!location.state?.address);
  const [error, setError] = useState('');
  const [successOpen, setSuccessOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const canSubmit = useMemo(
    () => Boolean(address?.address)
      && Number(amount) > 0
      && receiptPhoto
      && !submitting
      && !uploading,
    [address?.address, amount, receiptPhoto, submitting, uploading]
  );

  useEffect(() => {
    if (address) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    paymentApi
      .getOfflineDepositAddress({ id: addressId })
      .then((data) => {
        if (!cancelled) {
          setAddress(data || null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || t('deposit.loadAddressesFailed'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [address, addressId, t]);

  async function handleCopyAddress() {
    const ok = await copyText(address?.address);
    setCopied(ok);
    if (ok) {
      window.setTimeout(() => setCopied(false), 1500);
    }
  }

  async function handleUpload(event) {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    setUploading(true);
    setError('');
    try {
      const url = await memberApi.uploadOssImage(file);
      setReceiptPhoto(url || '');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('deposit.uploadFailed'));
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  }

  async function handleSubmit() {
    if (!address) {
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await paymentApi.submitOfflineDeposit({
        realAmount: Number(amount),
        currencyName: address.currencyName || undefined,
        receiptPhoto,
        address: address.address,
      });
      setSuccessOpen(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('deposit.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  }

  function handleSuccessClose() {
    setSuccessOpen(false);
    navigate('/wallet');
  }

  return (
    <div className="deposit-page deposit-page--form">
      <header className="deposit-page__header">
        <button
          type="button"
          className="deposit-page__back"
          aria-label={t('common.back')}
          onClick={() => navigate(-1)}
        >
          ‹
        </button>
        <h1>{t('deposit.offlineFormTitle')}</h1>
        <span className="deposit-page__header-spacer" />
      </header>

      {loading && <p className="deposit-page__status">{t('common.loading')}</p>}
      {error && <p className="deposit-page__error">{error}</p>}

      <DepositSuccessModal open={successOpen} onClose={handleSuccessClose} />

      {!loading && address && (
        <>
          <section className="deposit-field">
            <div className="deposit-field__label">{t('deposit.currency')}</div>
            <div className="deposit-currency-select deposit-currency-select--static">
              <span className="deposit-currency-select__left">
                {address.image ? (
                  <img src={resolveImageUrl(address.image)} alt="" />
                ) : (
                  <span className="deposit-currency-select__fallback">₮</span>
                )}
                <strong>{address.label}</strong>
              </span>
              <span className="deposit-currency-select__right">
                {tf(t, 'deposit.chainName', { chain: address.chainName || '—' })}
              </span>
            </div>
          </section>

          <section className="deposit-address-box">
            <div className="deposit-address-box__value">
              <span>{address.address}</span>
              <button type="button" onClick={handleCopyAddress}>
                {copied ? t('deposit.copied') : t('deposit.copy')}
              </button>
            </div>
            <div className="deposit-address-box__qr">
              <AddressQrCode value={address.address} size={220} />
            </div>
          </section>

          <section className="deposit-quick-amounts">
            <h2>{t('deposit.quickAmounts')}</h2>
            <div className="deposit-quick-amounts__grid">
              {DEFAULT_QUICK_AMOUNTS.map((value) => (
                <button
                  key={value}
                  type="button"
                  className="deposit-quick-amounts__btn"
                  onClick={() => setAmount(String(value))}
                >
                  {value}
                </button>
              ))}
            </div>
          </section>

          <section className="deposit-amount-card">
            <div className="deposit-amount-card__title">{t('deposit.amountTitle')}</div>
            <div className="deposit-amount-card__body">
              <div className="deposit-amount-card__input-row">
                <span className="deposit-amount-card__symbol">
                  {address.image ? (
                    <img src={resolveImageUrl(address.image)} alt="" />
                  ) : (
                    '₮'
                  )}
                </span>
                <input
                  type="number"
                  min="0"
                  step="0.0001"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                  placeholder="0"
                />
                <button
                  type="button"
                  className="deposit-amount-card__all"
                  onClick={() => setAmount('')}
                >
                  {t('deposit.reset')}
                </button>
              </div>
              <div className="deposit-amount-card__approx">
                ≈ {formatDepositAmount(amount)} ({address.currencyName || 'USDT'})
              </div>
            </div>
          </section>

          <p className="deposit-footnotes deposit-footnotes--single">{t('deposit.rateHint')}</p>

          <button
            type="button"
            className="deposit-upload-card"
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={handleUpload}
            />
            {receiptPhoto ? (
              <img src={resolveImageUrl(receiptPhoto)} alt={t('deposit.receiptAlt')} className="deposit-upload-card__preview" />
            ) : (
              <>
                <span className="deposit-upload-card__icon" aria-hidden>🖼</span>
                <span>{uploading ? t('common.loading') : t('deposit.uploadReceipt')}</span>
              </>
            )}
          </button>

          <button
            type="button"
            className={`deposit-submit ${canSubmit ? 'is-active' : ''}`}
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            {submitting ? t('common.loading') : t('deposit.submit')}
          </button>
        </>
      )}
    </div>
  );
}
